import { useEffect, useMemo, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"
import {
  createZeitbuchung,
  fetchAnlagen,
  fetchKomponente,
  fetchTicketKontext,
  fetchTimetabKomponenten,
  fetchTimetabLeistungen,
  fetchTimetabSchadenscodes,
} from "@/lib/api"
import { useAuth } from "@/auth/AuthProvider"
import { EmployeePicker, fetchAllEmployees } from "@/components/EmployeePicker"
import { FilterSelect } from "@/components/FilterSelect"
import { Button, Input, Textarea } from "@/components/ui"
import { useToast, fehlerNachricht } from "@/lib/toast"

// Issue #3 (Tester-Meldung #12): Stunden 0–12, Minuten in 10er-Schritten — als Buttons
const STUNDEN_OPTIONEN = Array.from({ length: 13 }, (_, i) => i)
const MINUTEN_OPTIONEN = [0, 10, 20, 30, 40, 50]

/** Zeit-Auswahl als anklickbare Buttons statt Dropdown (Issue #3) */
function ZeitButtons({
  optionen,
  wert,
  onChange,
}: {
  optionen: number[]
  wert: number
  onChange: (wert: number) => void
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {optionen.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          aria-pressed={wert === o}
          className={`h-9 min-w-9 rounded-md border px-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1 ${
            wert === o
              ? "border-slate-900 bg-slate-900 text-white"
              : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  )
}

/**
 * Zeiterfassungs-Formular (AP-3.7) — ersetzt die Ninox-Buchungsmaske (03-H).
 * Zustandslos: Persistenz erst beim Klick auf "Zeit buchen" (POST
 * /zeitbuchungen), keine serverseitige Zwischenspeicherung des Formulars
 * (ARCHITEKTUR.md §6). Eingabe-Verkettung Anlage→Komponente→Leistung→
 * Schadenscode über die timetab-Lookup-Endpoints (R-H-03/04/07/09/10/14).
 */
export function ZeiterfassungPage() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { user } = useAuth()
  const [searchParams] = useSearchParams()

  const [employeeId, setEmployeeId] = useState("")
  const [datum, setDatum] = useState(() => new Date().toISOString().slice(0, 10))
  const [anlageId, setAnlageId] = useState<number | "">("")
  const [komponenteId, setKomponenteId] = useState<number | "">("")
  const [leistungId, setLeistungId] = useState<number | "">("")
  const [schadenstyp, setSchadenstyp] = useState<string>("")
  const [schadenscodeId, setSchadenscodeId] = useState<number | "">("")
  const [stunden, setStunden] = useState(0)
  const [minuten, setMinuten] = useState(0)
  const [freitext, setFreitext] = useState("")
  const [ticketId, setTicketId] = useState("")
  const [mitarbeiterAbschliessen, setMitarbeiterAbschliessen] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)

  // Vorbelegung per URL-Parameter (CommandPalette „Zeit erfassen" am Suchtreffer):
  // /timetab?anlage=<id> | ?komponente=<id> | ?ticket=<id> — einmalig beim Öffnen.
  const urlVorbelegt = useRef(false)
  useEffect(() => {
    if (urlVorbelegt.current) return
    urlVorbelegt.current = true
    const ticket = searchParams.get("ticket")
    const komponente = searchParams.get("komponente")
    const anlage = searchParams.get("anlage")
    if (ticket) {
      setTicketId(ticket)
      fetchTicketKontext(Number(ticket))
        .then((k) => {
          if (k.anlageId) setAnlageId(k.anlageId)
          if (k.komponenteId) setKomponenteId(k.komponenteId)
        })
        .catch(() => {}) // Ticket ohne auflösbaren Kontext: Felder bleiben leer
    } else if (komponente) {
      fetchKomponente(Number(komponente))
        .then((k) => {
          setAnlageId(k.anlageId)
          setKomponenteId(k.id)
        })
        .catch(() => {})
    } else if (anlage) {
      setAnlageId(Number(anlage))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Issue #3: Mitarbeiter mit dem eingeloggten Benutzer vorbelegen (editierbar).
  // Zuordnung über den Namen (case-insensitiv); nur einmal und nur solange
  // das Feld unberührt ist — manuelles Leeren wird nicht übersteuert.
  const { data: alleMitarbeiter } = useQuery({
    queryKey: ["employees", "all"],
    queryFn: fetchAllEmployees,
    staleTime: 5 * 60_000,
  })
  const vorbelegt = useRef(false)
  useEffect(() => {
    if (vorbelegt.current || employeeId !== "" || !user?.name || !alleMitarbeiter) return
    const gesucht = user.name.trim().toLocaleLowerCase("de")
    const treffer = alleMitarbeiter.find(
      (e) => e.aktiv && e.name.trim().toLocaleLowerCase("de") === gesucht
    )
    if (treffer) {
      setEmployeeId(treffer.id)
      vorbelegt.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alleMitarbeiter, user?.name])

  const { data: anlagen } = useQuery({ queryKey: ["anlagen"], queryFn: fetchAnlagen })
  const { data: komponenten } = useQuery({
    queryKey: ["timetab-komponenten", anlageId],
    queryFn: () => fetchTimetabKomponenten(anlageId === "" ? undefined : anlageId),
    enabled: anlageId !== "",
  })
  const { data: leistungen } = useQuery({
    queryKey: ["timetab-leistungen"],
    queryFn: fetchTimetabLeistungen,
  })
  const { data: schadenscodes } = useQuery({
    queryKey: ["timetab-schadenscodes", schadenstyp],
    queryFn: () => fetchTimetabSchadenscodes(schadenstyp || undefined),
    enabled: schadenstyp !== "",
  })

  const zeitVorschau = useMemo(() => {
    return (stunden + minuten / 60).toFixed(2)
  }, [stunden, minuten])

  const mutation = useMutation({
    mutationFn: createZeitbuchung,
    onSuccess: () => {
      setMeldung("Zeitbuchung gespeichert.")
      toast.success("Zeitbuchung gespeichert.")
      queryClient.invalidateQueries({ queryKey: ["zeitbuchungen"] })
      // R-H-BTN-02 „Maske leeren": Formular zurücksetzen, Datum/Uhrzeit neu.
      setKomponenteId("")
      setLeistungId("")
      setSchadenstyp("")
      setSchadenscodeId("")
      setStunden(0)
      setMinuten(0)
      setFreitext("")
      setTicketId("")
      setMitarbeiterAbschliessen(false)
    },
    onError: (err) => {
      setMeldung("Zeitbuchung konnte nicht gespeichert werden.")
      toast.error(fehlerNachricht(err, "Zeitbuchung konnte nicht gespeichert werden."))
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setMeldung(null)
    if (!employeeId || komponenteId === "" || leistungId === "") {
      setMeldung("Mitarbeiter, Komponente und Leistung sind Pflichtfelder.")
      return
    }
    mutation.mutate({
      employeeId,
      datum,
      stunden,
      minuten,
      komponenteId: Number(komponenteId),
      leistungId: Number(leistungId),
      schadenstyp: schadenstyp || null,
      schadenscodeId: schadenscodeId === "" ? null : Number(schadenscodeId),
      freitext: freitext || null,
      ticketId: ticketId === "" ? null : Number(ticketId),
      mitarbeiterAbschliessen: ticketId !== "" ? mitarbeiterAbschliessen : false,
    })
  }

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Zeit erfassen</h1>
        <p className="mt-1 text-sm text-slate-600">
          Zustandsloses Formular (03-F1) — Persistenz erst beim Buchen, keine Buchungsmaske.
        </p>
      </header>

      {/* Issue #4: Formular volle Breite, rechts Hilfe-Bereich (nur diese Seite) */}
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
      <form
        onSubmit={handleSubmit}
        className="space-y-4 rounded-lg border border-slate-200 bg-white p-6"
      >
        <div className="grid grid-cols-2 gap-4">
          <EmployeePicker
            value={employeeId || null}
            onChange={(id) => setEmployeeId(id ?? "")}
            required
          />
          <label className="block text-sm">
            <span className="mb-1 block text-slate-700">Datum</span>
            <Input
              type="date"
              value={datum}
              onChange={(e) => setDatum(e.target.value)}
              required
            />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <label className="block text-sm">
            <span className="mb-1 block text-slate-700">Anlage</span>
            <FilterSelect
              optionen={(anlagen ?? []).map((a) => ({
                wert: String(a.id),
                label: a.bezeichnung,
              }))}
              wert={anlageId === "" ? "" : String(anlageId)}
              onChange={(w) => {
                setAnlageId(w === "" ? "" : Number(w))
                setKomponenteId("")
              }}
              placeholder="— wählen (tippen filtert) —"
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-slate-700">Komponente</span>
            <FilterSelect
              optionen={(komponenten ?? []).map((k) => ({
                wert: String(k.id),
                label: `${k.beschreibung}${k.tagNummer ? ` (${k.tagNummer})` : ""}`,
              }))}
              wert={komponenteId === "" ? "" : String(komponenteId)}
              onChange={(w) => setKomponenteId(w === "" ? "" : Number(w))}
              disabled={anlageId === ""}
              required
              placeholder="— wählen (tippen filtert) —"
            />
          </label>
        </div>

        <label className="block text-sm">
          <span className="mb-1 block text-slate-700">Leistung</span>
          <FilterSelect
            optionen={(leistungen ?? []).map((l) => ({
              wert: String(l.id),
              label: l.leistung,
            }))}
            wert={leistungId === "" ? "" : String(leistungId)}
            onChange={(w) => setLeistungId(w === "" ? "" : Number(w))}
            required
            placeholder="— wählen (tippen filtert) —"
          />
        </label>

        <div className="grid grid-cols-2 gap-4">
          <label className="block text-sm">
            <span className="mb-1 block text-slate-700">Schadenstyp (optional)</span>
            <FilterSelect
              optionen={[
                { wert: "elektrisch", label: "elektrisch" },
                { wert: "mechanisch", label: "mechanisch" },
              ]}
              wert={schadenstyp}
              onChange={(w) => {
                setSchadenstyp(w)
                setSchadenscodeId("")
              }}
              placeholder="— keiner —"
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-slate-700">Schadenscode</span>
            <FilterSelect
              optionen={(schadenscodes ?? []).map((c) => ({
                wert: String(c.id),
                label: c.schadensbezeichnung,
              }))}
              wert={schadenscodeId === "" ? "" : String(schadenscodeId)}
              onChange={(w) => setSchadenscodeId(w === "" ? "" : Number(w))}
              disabled={schadenstyp === ""}
              placeholder="— keiner —"
            />
          </label>
        </div>

        <div className="space-y-3">
          <div className="text-sm">
            <span className="mb-1 block text-slate-700">Stunden</span>
            <ZeitButtons optionen={STUNDEN_OPTIONEN} wert={stunden} onChange={setStunden} />
          </div>
          <div className="text-sm">
            <span className="mb-1 block text-slate-700">Minuten</span>
            <ZeitButtons optionen={MINUTEN_OPTIONEN} wert={minuten} onChange={setMinuten} />
          </div>
        </div>

        <p className="text-sm text-slate-500">Zeit (Vorschau): {zeitVorschau} h</p>

        <label className="block text-sm">
          <span className="mb-1 block text-slate-700">Serviceticket-Bezug (optional, 03-F4)</span>
          <Input
            type="number"
            value={ticketId}
            onChange={(e) => setTicketId(e.target.value)}
            placeholder="Ticket-ID"
          />
        </label>

        {ticketId !== "" && (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={mitarbeiterAbschliessen}
              onChange={(e) => setMitarbeiterAbschliessen(e.target.checked)}
              className="rounded-md focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1 focus-visible:outline-none"
            />
            Meinen Anteil am Ticket mit dieser Buchung abschließen (AP-4.6, ersetzt den
            impliziten Ninox-Effekt beim Tab-Verlassen)
          </label>
        )}

        <label className="block text-sm">
          <span className="mb-1 block text-slate-700">Freitext (optional)</span>
          <Textarea value={freitext} onChange={(e) => setFreitext(e.target.value)} rows={2} />
        </label>

        {meldung && <p className="text-sm text-slate-700">{meldung}</p>}

        <Button type="submit" disabled={mutation.isPending} variant="primary">
          Zeit buchen
        </Button>
      </form>

      {/* Hilfe-Bereich (Issue #4): Erläuterungen zum Formular */}
      <aside className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-700 lg:sticky lg:top-4">
        <h2 className="mb-3 text-base font-semibold text-slate-900">Hilfe zur Zeiterfassung</h2>
        <dl className="space-y-3">
          <div>
            <dt className="font-medium text-slate-900">Mitarbeiter</dt>
            <dd>
              Ist mit deinem Konto vorbelegt, bleibt aber änderbar — z. B. um Zeiten für
              Kollegen zu erfassen. Tippen filtert nach Name oder Kürzel.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-slate-900">Anlage → Komponente</dt>
            <dd>
              Erst die Anlage wählen, dann werden die zugehörigen Komponenten geladen.
              In beiden Feldern kannst du durch Tippen filtern (Treffer irgendwo im Text).
            </dd>
          </div>
          <div>
            <dt className="font-medium text-slate-900">Leistung</dt>
            <dd>Pflichtfeld — die Art der ausgeführten Arbeit, ebenfalls filterbar.</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-900">Schadenstyp &amp; Schadenscode</dt>
            <dd>
              Optional: erst den Typ (elektrisch/mechanisch) wählen, dann die passenden
              Codes. Ohne Schadensbezug einfach leer lassen.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-slate-900">Stunden &amp; Minuten</dt>
            <dd>
              Dauer per Klick wählen: Stunden 0–12, Minuten in 10er-Schritten. Die
              Vorschau darunter zeigt die Dezimalzeit, die gebucht wird.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-slate-900">Serviceticket-Bezug</dt>
            <dd>
              Optional eine Ticket-ID angeben, um die Buchung dem Ticket zuzuordnen.
              Mit dem Haken schließt du deinen Anteil am Ticket direkt mit ab.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-slate-900">Speichern</dt>
            <dd>
              „Zeit buchen" speichert die Buchung und leert die Maske für die nächste
              Erfassung — Mitarbeiter, Datum und Anlage bleiben stehen.
            </dd>
          </div>
        </dl>
      </aside>
      </div>
    </div>
  )
}
